/* iter-mail-routing-diag — Admin panel that shows Cloudflare inbound
 * plumbing state (secret set? which addresses route where? recent
 * inbound? checklist of dashboard steps still required?) and a
 * loopback POST that proves the pipe end-to-end.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, CheckCircle2, XCircle, AlertTriangle, Zap } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

export default function MailRoutingDiagnostics() {
  const [diag, setDiag] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loopTo, setLoopTo] = useState('d.willis@crownnrose.com');

  const refresh = async () => {
    setBusy(true);
    try {
      const r = await http.get('/admin/email-inbound/diagnostics');
      setDiag(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Diagnostics unavailable.');
    } finally { setBusy(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const loopback = async () => {
    try {
      await http.post('/admin/email-inbound/loopback-test', { to: loopTo });
      toast.success('Loopback dispatched — refresh /admin/inbox to see the thread.');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Loopback failed.');
    }
  };

  if (!diag) return (
    <div className="flex h-32 items-center justify-center text-[12px] text-muted-foreground">
      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading routing diagnostics…
    </div>
  );

  const iconFor = (ok) => ok === true
    ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
    : ok === false
      ? <XCircle className="h-3.5 w-3.5 text-red-500" />
      : <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />;

  return (
    <section className="rounded-lg border border-border bg-card p-4" data-testid="mail-routing-diag">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-[13px] font-semibold">Inbound mail routing diagnostics</div>
        <div className="flex gap-1.5">
          <button type="button" onClick={refresh} disabled={busy}
                  data-testid="mail-diag-refresh"
                  className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-1 text-[11px] hover:bg-secondary">
            <RefreshCw className={`h-3 w-3 ${busy ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-4 text-[12px]">
        <div className="rounded-md border border-border bg-panel-header/40 p-2">
          <div className="text-[10px] uppercase text-muted-foreground">Webhook</div>
          <div className="font-mono text-[11px]">{diag.webhook_url}</div>
        </div>
        <div className="rounded-md border border-border bg-panel-header/40 p-2">
          <div className="text-[10px] uppercase text-muted-foreground">Shared secret</div>
          <div className={diag.secret_set ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-600'}>
            {diag.secret_set ? 'Set ✓' : 'NOT set'}
          </div>
        </div>
        <div className="rounded-md border border-border bg-panel-header/40 p-2">
          <div className="text-[10px] uppercase text-muted-foreground">Inbound · 24h</div>
          <div className="font-semibold">{diag.traffic.last_24h.inbound_messages}</div>
        </div>
        <div className="rounded-md border border-border bg-panel-header/40 p-2">
          <div className="text-[10px] uppercase text-muted-foreground">Threads · 30d</div>
          <div className="font-semibold">{diag.traffic.last_30d.new_threads}</div>
        </div>
      </div>

      <div className="mb-4">
        <div className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">Hardcoded business addresses</div>
        <table className="w-full text-[12px]" data-testid="mail-diag-addresses">
          <thead>
            <tr className="border-b border-border text-left text-[10.5px] uppercase text-muted-foreground">
              <th className="py-1">Address</th><th>Label</th><th>Routes to</th>
            </tr>
          </thead>
          <tbody>
            {diag.hardcoded_addresses.map((a) => (
              <tr key={a.email} className="border-b border-border/60">
                <td className="py-1 font-mono text-[11px]">{a.email}</td>
                <td className="text-muted-foreground">{a.label || '—'}</td>
                <td>{a.routes_to}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mb-4">
        <div className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">Setup checklist</div>
        <ul className="space-y-1.5">
          {diag.checklist.map((c, i) => (
            <li key={i} className="flex items-start gap-2 rounded-md border border-border p-2 text-[12px]">
              <span className="mt-0.5">{iconFor(c.ok)}</span>
              <div className="flex-1">
                <div className="font-medium">{c.step}</div>
                {c.fix && <div className="mt-0.5 text-[11px] text-muted-foreground">{c.fix}</div>}
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-md border border-primary/30 bg-primary/5 p-3">
        <div className="mb-2 flex items-center gap-2 text-[12px] font-semibold">
          <Zap className="h-3.5 w-3.5 text-primary" /> Loopback smoke test
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input type="text" value={loopTo} onChange={(e) => setLoopTo(e.target.value)}
                 data-testid="mail-diag-loop-to"
                 className="h-8 flex-1 min-w-[240px] rounded-sm border border-border bg-background px-2 text-[12px]" />
          <button type="button" onClick={loopback} disabled={!diag.secret_set}
                  data-testid="mail-diag-loop-btn"
                  className="rounded-sm bg-primary px-3 py-1 text-[11.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
            Fire loopback
          </button>
        </div>
        <div className="mt-1 text-[10.5px] text-muted-foreground">
          POSTs directly to /webhooks/email-inbound with the shared secret so you can verify the pipe end-to-end without waiting for real mail.
        </div>
      </div>

      {diag.recent_inbound.length > 0 && (
        <div className="mt-4">
          <div className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">Latest inbound (5)</div>
          <table className="w-full text-[12px]" data-testid="mail-diag-recent">
            <thead>
              <tr className="border-b border-border text-left text-[10.5px] uppercase text-muted-foreground">
                <th className="py-1">At</th><th>From</th><th>To</th><th>Subject</th>
              </tr>
            </thead>
            <tbody>
              {diag.recent_inbound.map((m, i) => (
                <tr key={i} className="border-b border-border/60">
                  <td className="py-1 font-mono text-[10.5px]">{(m.at || '').slice(0, 16).replace('T', ' ')}</td>
                  <td className="truncate">{m.from_addr}</td>
                  <td className="truncate">{m.to_addr}</td>
                  <td className="truncate max-w-xs">{m.subject}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
